<template>
	<div
		class="cog-picker cog-select cog-input"
		:class="[
			{'is-read-only': readonly, 'cog-select--has-image': hasImage(selectedValue), 'cog-picker--allow-other': internalAllowOther, 'cog-select--hidden-label': hideChoiceLabels && hasImage(selectedValue)},
			'cog-image-size--' + (readonly ? choiceImageSize + 1 : '1'),
		]"
	>
		<div v-if="readonly" class="cog-choice-option"><CChoiceImage v-if="selectedValue && hasImage(selectedValue)" :option="selectedValue" v-bind="{isPersonField, choiceImageSize, fontSize}" /><div v-if="displayValue && !(hideChoiceLabels && hasImage(selectedValue))">{{ displayValue | breakWords }}</div></div>
		<template v-else>
			<c-picker-native v-if="useNative" :placeholder="placeholder" :show-placeholder="!hidePlaceholder">
				<template v-if="internalAllowOther">
					<input
						:id="fullId"
						v-model="internalValue"
						type="text"
						:list="id ? `cog-${id}-list` : null"
						:placeholder="placeholder"
						class="el-input__inner"
						:aria-required="isRequired ? true : undefined"
						:aria-describedby="ariaDescriptions"
						:aria-invalid="hasError ? true : undefined"
						@keydown="handleNativeMenuOpen"
						@click="handleNativeMenuOpen"
						@change="handleNativeChange"
						@focus="handleFocus"
						@blur="handleBlur"
					/>
					<datalist :id="id ? `cog-${id}-list` : null">
						<option v-if="showEmptyOption" :value="null">&nbsp;</option>
						<option v-for="option in options" :key="option.value" :value="option.value">{{ option.displayValue }}</option>
					</datalist>
				</template>
				<select
					v-else
					:id="fullId"
					ref="nativeSelect"
					v-model="internalValue"
					v-bind="$attrs"
					:title="title"
					class="el-input__inner"
					:class="{'is-null': !value}"
					:aria-required="isRequired ? true : undefined"
					:aria-describedby="ariaDescriptions"
					:aria-invalid="hasError ? true : undefined"
					@keydown="handleNativeMenuOpen"
					@click="handleNativeMenuOpen"
					@change="handleNativeChange"
					@focus="handleFocus"
					@blur="handleBlur"
				>
					<option v-if="showEmptyOption" :value="null">&nbsp;</option>
					<option v-for="option in options" :key="option.value" :value="option.value">{{ option.displayValue }}</option>
				</select>
				<template v-if="mobile && showSelectIcon" #icon>
					<i-chevron />
				</template>
			</c-picker-native>
			<!--	For autocomplete drama, see first comment on #17543	-->
			<el-select
				v-else
				:id="fullId"
				ref="select"
				v-model="internalValue"
				v-bind="$attrs"
				:placeholder="placeholder"
				:allow-create="internalAllowOther"
				no-data-text=" "
				default-first-option
				:filterable="filterable"
				:filter-match="filterMatch"
				:alternative-filter="alternativeFilter"
				:editable="internalAllowOther"
				:automatic-dropdown="false"
				:popper-class="popperClasses"
				:autocomplete="autocomplete"
				:title="title"
				:aria-required="isRequired ? true : undefined"
				:aria-describedby="ariaDescriptions"
				:aria-invalid="hasError ? true : undefined"
				lazy-options
				@focus="handleFocus"
				@blur="handleBlur"
				@change="handleChange"
				@change.native="handleUserInputChange"
				@visible-change="handleSelectVisibleChange"
			>
				<el-option v-if="showEmptyOption" :value="null" class="cog-select-option--empty">&nbsp;</el-option>
				<el-option
					v-for="(option, index) in options"
					:key="option.value + index"
					:value="option.value"
					:label="option.displayValue || nbsp"
					:style="{ order: index}"
				>
					<div class="cog-choice-option" :class="{'cog-choice-option--no-image': hasImageOption && !option.choiceThumbnailImage}">
						<CChoiceImage v-if="hasImage(option)" v-bind="{option, isPersonField, choiceImageSize, fontSize}" />
						<div v-else-if="hasImageOption" class="cog-choice-image-container"></div>
						<span v-if="!(hideChoiceLabels && hasImage(option))">{{ option.displayValue || option.value || nbsp }}</span>
					</div>
				</el-option>
				<template
					v-if="!prefixDefined && hasImageOption && !dropdownVisible && selectedValue && hasImage(selectedValue)" #prefix
				>
					<CChoiceImage
						:option="selectedValue"
						:choice-image-size="0"
						v-bind="{isPersonField, fontSize}"
						class="cog-select__prefix-image"
					/>
				</template>
				<template v-if="showSelectIcon" #suffix>
					<span @click="handleIconClick" @mousedown="handleIconMousedown">
						<slot name="suffix">
							<i-chevron />
						</slot>
					</span>
				</template>
			</el-select>
		</template>
	</div>
</template>

<script>
	import 'src/style/_popper.scss';
	import 'src/style/_motion.scss';
	import 'src/style/_input-icon.scss';
	import 'src/style/_scrollbar.scss';
	import 'src/style/_dropdown.scss';

	import Vue from 'vue';
	import { Option } from '@cognitoforms/element-ui';
	import CPickerNative from 'src/components/PickerNative.vue';
	import IChevron from './../assets/chevron.svg';
	import focus from '../mixins/focus.ts';
	import CSelectInput from './SelectInput.ts';
	import CChoiceImage from './ChoiceImage.vue';
	import { ensureMinHeight } from './form-resizing';
	import { isSafari } from '@cognitoforms/utils/user-agent';
	import { getChoiceImageSizeClass } from './use-choice-image-size';

	Vue.use(Option);

	const MIN_REFOCUS_DELAY = 25;
	const SELECT_MENU_MAXHEIGHT = 200;
	const NATIVE_SELECT_OPTIONS_THRESHOLD = 500;

	export default {
		name: 'CSelect',
		components: { IChevron, CPickerNative, 'el-select': CSelectInput, CChoiceImage },
		mixins: [focus],
		inject: {
			$resource: { default: () => ({}) },
			formEvents: { default: () => ({}) },
			flags: { default: () => ({}) },
			fieldContext: { default: () => () => ({ hasError: false, isRequired: false }) }
		},
		inheritAttrs: false,
		model: {
			prop: 'value',
			event: 'change'
		},
		props: {
			// eslint-disable-next-line vue/require-prop-types
			value: { default: '' },
			readonly: Boolean,
			allowOther: Boolean,
			chameleon: Boolean,
			mobile: Boolean,
			showEmptyOption: { type: Boolean, default: true },
			allowArrowsToChangeValue: { type: Boolean, default: true },
			placeholder: { type: String, default: null },
			options: { type: Array, default: null },
			scope: { type: String, default: '0' },
			additionalPopperClass: { type: String, default: '' },
			filterable: { type: Boolean, default: true },
			filterMatch: { type: String, default: 'contains' },
			id: { type: String, required: true },
			title: { type: String, default: null },
			autocomplete: { type: String, default: 'prevent' },
			alternativeFilter: { type: Function, default: null },
			choiceImageSize: { type: Number, default: 0 },
			hideChoiceLabels: { type: Boolean, default: false },
			isPersonField: { type: Boolean, default: false },
			imagesInChoiceFieldsEnabled: { type: Boolean, default: false }
		},
		data() {
			return {
				internalValue: this.value,
				hidePlaceholder: this.allowOther || !!this.value,
				internalAllowOther: this.allowOther,
				dropdownVisible: false,
				fontSize: undefined
			};
		},
		computed: {
			showSelectIcon() {
				// Non-number/text dropdown field (ie: time field) icons should always be displayed
				if (this.$slots.suffix)
					return true;

				return !this.internalAllowOther;
			},
			fullId() {
				return this.id ? 'cog-' + this.id : null;
			},
			fullSourceId() {
				return this.fieldContext().id ? 'cog-' + this.fieldContext().id : null;
			},
			hasError() {
				return this.fieldContext().hasError;
			},
			isRequired() {
				return this.fieldContext().isRequired;
			},
			hasHelptext() {
				return this.fieldContext().hasHelptext;
			},
			ariaDescriptions() {
				const ids = [
					this.hasError ? `${this.fullSourceId}-error` : null,
					this.hasHelptext ? `${this.fullSourceId}-helptext` : null
				].filter(Boolean);
				return ids.length ? ids.join(' ') : undefined;
			},
			displayValue() {
				if (this.value && Array.isArray(this.options)) {
					const selected = this.options.find(o => o.value === this.value);
					return selected ? selected.displayValue : this.value;
				}
				return '';
			},
			selectedValue() {
				if (this.value && Array.isArray(this.options))
					return this.options.find(o => o.value === this.value);
				return null;
			},
			useNative() {
				const optionCount = this.options?.length ?? 0;

				if (this.hasImageOption) return false;
				else if (isSafari(window.navigator) && this.allowOther) return false;
				else if (this.chameleon) return true;
				else if (this.mobile && optionCount < NATIVE_SELECT_OPTIONS_THRESHOLD) return true;
				else return false;
			},
			nbsp() {
				return '\u00A0';
			},
			popperClasses() {
				return [`cog-cognito cog-${this.scope}`,
					`cog-cognito--${(this.chameleon ? 'chameleon' : 'styled')}`,
					'cog-select-popper',
					`${this.options && this.options.length > 100 ? 'el-select-dropdown--long' : ''}`,
					`${this.flags.protectCss ? 'cog-cognito--protect-css' : ''}`,
					`${this.additionalPopperClass}`,
					this.choiceImageSizeClass
				].join(' ');
			},
			hasImageOption() {
				return this.options?.some(dropdownOption => dropdownOption.thumbnailImage || dropdownOption.svg || dropdownOption.choiceThumbnailImage);
			},
			choiceImageSizeClass() {
				return getChoiceImageSizeClass(this.choiceImageSize);
			},
			prefixDefined() {
				return !!this.$slots.prefix;
			}
		},
		watch: {
			value: {
				immediate: true,
				handler: function(valueProp) {
					// Selects can't contain a null value so blank will need to be converted
					this.internalValue = valueProp === '' ? null : valueProp;
					this.hidePlaceholder = this.internalAllowOther || !!this.value;
				}
			}
		},
		mounted() {
			// Font size of the picker not the dropdown menu but will probably be the same
			this.fontSize = parseInt(getComputedStyle(this.$el).fontSize);
		},
		methods: {
			// Allows a user to type in a value and then click outside of the select to set a value
			handleUserInputChange(e) {
				if (!this.internalAllowOther) {
					const matchedOption = this.options.filter((o)=>
						o.value.toLowerCase() === e.target.value.toLowerCase() ||
						o.displayValue.toLowerCase() === e.target.value.toLowerCase()
					)[0];

					if (matchedOption)
						this.handleChange(matchedOption.value);
				}
			},
			handleBlur(e) {
				this.removeClassOnBlur();
				this.$emit('blur', e);
				this.hidePlaceholder = this.internalAllowOther || !!this.value;
			},
			handleFocus(e) {
				this.addClassOnFocus();
				this.$emit('focus', e);
			},
			handleChange(value) {
				this.internalValue = value;
				this.$emit('change', value);
			},
			handleNativeMenuOpen() {
				// Native select (on desktop) can overlap with faux placeholder text. See #18599
				this.hidePlaceholder = true;
			},
			handleNativeChange(ev) {
				// IOS needs the select to be refocused after selection if "done" is tapped. Cf. #20786
				// Any number less than 20 results in inconsistent success.
				setTimeout(() => {
					if (document.activeElement === document.body)
						this.$refs.nativeSelect.focus();
				}, MIN_REFOCUS_DELAY);
				const value = ev.target.value;
				this.internalValue = value;
				this.$emit('change', value, ev);
			},
			handleIconClick(e) {
				// toggle menu
				this.$refs.select.visible = !this.$refs.select.visible;
				e.stopPropagation();

				// keep focus on select regardless
				this.$refs.select.$el.querySelector('input').focus();
			},
			handleIconMousedown(e) {
				// prevent handleBlur from being called as a result of clicking the chevron
				e.preventDefault();
			},
			handleSelectVisibleChange(visible) {
				// Re-emit the "visible-change" event
				this.$emit('visible-change', visible);
				if (this.formEvents)
					if (!visible) {
						this.dropdownVisible = false;
						ensureMinHeight(this.formEvents, 0);
					}
					else {
						this.dropdownVisible = true;
						const formHeight = document.documentElement.clientHeight;
						const { top: selectTop, bottom: selectBottom } = this.$el.getBoundingClientRect();
						const selectBottomBounds = Math.ceil(selectBottom) + SELECT_MENU_MAXHEIGHT;
						// If there's no room on top or bottom of the form for the select menu
						if (Math.ceil(selectTop) < SELECT_MENU_MAXHEIGHT && selectBottomBounds > formHeight)
							// Make the form tall enough to accomodate the select menu on the bottom
							ensureMinHeight(this.formEvents, selectBottomBounds);
					}
			},
			hasImage(item) {
				return item && (item.svg || item.thumbnailImage || item.choiceThumbnailImage);
			}
		}
	};
</script>

<style lang="scss">
$SELECT_MENU_MAXHEIGHT: 200px;

#{$specificity-base} {

	.cog-picker--allow-other {

		.el-input__inner {
			cursor: text;
		}
	}

	@include at-root {

		&.cog-iframe-embed .el-select-dropdown__wrap {
			max-height: $SELECT_MENU_MAXHEIGHT;
		}
	}

	.cog-cognito--chameleon select {
		width: 100%;
	}

	.cog-select--hidden-label .el-input:not(.is-focus) input {
		color: transparent !important;
	}

	.cog-select__prefix-image > * {
		position: relative;
	}

	.cog-image-item__dropdown {
		margin-left: -.3em;
	}

	.cog-select,
	.cog-select-popper {

		.cog-choice-image-container {
			width: var(--image-size);
		}
	}
}
</style>
